import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FormikProps } from "formik"
import { useCallback, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { NoDataFallback, QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { ErrorDisplay, NoDataError, SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { TENANT_ADMIN_ROUTE, TENANT_USERS_ROUTE, getTenantUsersRoute } from "../../../../../../constants/routes"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { selectEffectiveCompanyId, selectEffectiveUserGroup } from "../../../../../context/contextSelectors"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppSelector } from "../../../../../store/storeHooks"
import { GetUserDetailsResponse, getUserDetails, updateUser } from "../../../../../users/userApi"
import { TenantAdminQuery } from "../../../tenantAdminApi"

import { TenantUserForm } from "../components/TenantUserForm"
import { TenantUserFormValues } from "../tenantUserTypes"
import { toTenantUserFormValues, toUpdateTenantUserBody } from "../utils/tenantUserBody"

export function EditTenantUserPage() {
    const navigate = useNavigate()
    const { userId: userIdParam } = useParams<{ userId?: string }>()
    const userId = Number(userIdParam)
    const isValidUserId = Number.isFinite(userId)

    const companyId = useAppSelector(selectEffectiveCompanyId)
    const userGroupId = useAppSelector(selectEffectiveUserGroup)

    const formRef = useRef<FormikProps<TenantUserFormValues>>(null)

    /**
     * `getUserDetails` serves every portal, so a user id in the URL is not proof that the user belongs to this
     * tenant. Narrowing here means a foreign id renders the no-data fallback instead of an editable stranger.
     */
    const selectTenantUser = useCallback(
        ({ user }: GetUserDetailsResponse) => {
            const belongsToTenant =
                companyId !== undefined && user.companyId === companyId && user.customerTypeId === userGroupId

            if (!belongsToTenant) throw new NoDataError()

            return {
                user,
                initialValues: toTenantUserFormValues(user),
            }
        },
        [companyId, userGroupId],
    )

    const userResult = useApiQuery(getUserDetails, {
        queryName: TenantAdminQuery.DETAILS_FOR_USER,
        dependencies: { userId },
        enabled: isValidUserId,
        select: selectTenantUser,
        errorDisplay: ErrorDisplay.NONE,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateUser, isLoading: isUpdating } = useApiMutation(updateUser, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, _, context) {
            await context.queryClient.invalidateQueries({ queryKey: [TenantAdminQuery.USERS_FOR_TENANT] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(TenantAdminQuery.DETAILS_FOR_USER, { userId: params.userId }),
            })

            navigate(getTenantUsersRoute("user"))
        },
    })

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    async function handleSubmit(values: TenantUserFormValues) {
        if (!userResult.data) return

        await callUpdateUser({
            userId: userResult.data.user.id,
            user: toUpdateTenantUserBody(values, userResult.data.user),
        })
    }

    return (
        <>
            <LocalizedStrict id="office-manager-edit-user-form-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.TENANT_EDIT_USER}
                    title="Edit user"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    rightElement={
                        <LocalizedStrict id="office-manager-edit-user-form">
                            <StandardLoadingButton
                                type="submit"
                                onClick={handleSaveClick}
                                loading={isUpdating}
                                disabled={!userResult.data}
                            >
                                Save
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="office-manager-create-user-home-breadcrumb">
                            <BreadcrumbLink to={TENANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="tenant-nav-item-organization-header">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="office-manager-create-user-users-breadcrumb">
                            <BreadcrumbLink to={TENANT_USERS_ROUTE}>Users</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="office-manager-create-user-edit-breadcrumb">
                            <Breadcrumb>Edit user</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary dependencies={{ userId }}>
                {!isValidUserId && <NoDataFallback />}
                {isValidUserId &&
                    (userResult.data ? (
                        <TenantUserForm
                            ref={formRef}
                            isEditMode
                            initialValues={userResult.data.initialValues}
                            history={userResult.data.user.history}
                            onSubmit={handleSubmit}
                        />
                    ) : (
                        <LoadingSpinner />
                    ))}
            </QueryErrorBoundary>
        </>
    )
}
